import {_, React, observer, observable, computed, IDisposer, action, useEffect, classNames, useAsObservableSource, Popover2, Fuse} from '@api'
import {Button, Card, CardContent} from '@material-ui/core'
import {ArrowDropDown, ArrowDropUp} from '@material-ui/icons'
import {ContentTitleBarItemField, ChangeLogMenuItemSelected, useVipLocalStore, VipLocalStore, SearchPopover, useContentTitleBarItemFieldStore, ISortWithOrder, IChangeLogFieldConfig} from '@modules'
import {ICatalogFieldConfig, shouldIgnoreCatalogField, StoresInstance, WorkspaceChangeLogAdditionalConfigType} from '@stores'
import {SearchField2} from 'centric-ui-components'
import * as css from './ContentTitleBarItem.css'

/// region Store

export type ChangeLogFilterFieldConfig = ICatalogFieldConfig & WorkspaceChangeLogAdditionalConfigType

export class ContentTitleBarItemStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get title() {
    return this.props.title
  }

  @computed get workspaceIndexes(): Array<string> {
    const {workspaces: {workspaceByIndex}, indexes} = this

    return indexes.length ? indexes : Object.keys(workspaceByIndex)
  }

  get selected() {
    return this.props.selected
  }

  @computed get selectedValues() {
    const {selected, props: {sortWithOrder, feature}} = this,
      values: Array<ChangeLogFilterFieldConfig> = Array.from(selected.values())
    if (feature == 'sort') {
      return _.map(sortWithOrder, s => s.field)
    } else {
      return values
    }
  }

  @action onSelectMenuItem = (key, field: ChangeLogFilterFieldConfig, selectedField?: ChangeLogFilterFieldConfig) => {
    const {props: {onSelectMenuItem}} = this
    if (_.isFunction(onSelectMenuItem)) onSelectMenuItem(key, field, selectedField)
  }

  @computed get config() {
    const {workspaces: {changeLogConfig}} = this
    return changeLogConfig
  }

  @computed get additionalFields() : Array<IChangeLogFieldConfig & {workspace: 'additional', workspaceLabel: ''}> {
    const {config: {additional}} = this

    return additional?.map(field => ({...field, workspace: 'additional', workspaceLabel: ''}))
  }

  @computed get fields(): Array<ChangeLogFilterFieldConfig> {
    const {additionalFields, workspaceIndexes, workspaces: {workspaceByIndex}, selected} = this

    return [
      ...additionalFields, ...workspaceIndexes.reduce((acc, index) => {
        const {catalog: {fieldBySection, sectionById, sectionValuesFor, workspaceConfig, catalogItemName}} = _.first(workspaceByIndex[index]),
          fields = fieldBySection[workspaceConfig._id],
          f = fields.filter(n => !shouldIgnoreCatalogField(workspaceConfig, n.field) && !n?.card?.hide && !n?.agg).map(field => ({...field, workspace: index, workspaceLabel: catalogItemName})),
          sections = Object.keys(sectionById).reduce((acc, key) => {
            const sectionValues = sectionValuesFor(key)
            if (sectionValues !== undefined) {
              const values = fieldBySection[key].filter(n => !shouldIgnoreCatalogField(workspaceConfig, n.field) && !n?.card?.hide && !n.agg)
              return [
                ...acc,
                ...values.map((val: any) => {
                  // val.workspace = workspaceConfig.catalog.index
                  val.workspaceLabel = catalogItemName
                  val.values = sectionValues
                  return val
                })
              ]
            }
            return acc
          }, [])
        return [
          ...acc,
          ...f,
          ...sections
        ]
      }, [])
    ]
  }

  @observable private fuse = new Fuse(this.fields, {
    keys: ['label'],
    threshold: 0.4
    // includeMatches: true,
    // findAllMatches: true
  })

  get indexes() {
    return this.props.indexes
  }

  @computed get fieldsList() {
    const {searchText, fuse, fields, catalog: {labelForField}} = this,
      searchResults = fuse.search(searchText).map(r => r.item),
      groups = _.groupBy((searchText ? searchResults : fields), 'workspaceLabel'),
      keys = Object.keys(groups)

    return keys.reduce((acc, key) => {
      const items = _.orderBy(groups[key], g => _.trim(labelForField(g) ?? g.label), 'asc')
      return [
        ...acc,
        ...items
      ]
    }, [])
  }

  @observable searchText: string = ''

  @action onSearch = (event) => {
    const {target: {value: text}} = event
    this.searchText = text
  }

  @observable open: boolean = false

  onClickOpenMenuItemList = (open?: boolean) => {
    this.open = open !== undefined ? open : !this.open
  }

  @computed get menuTitle() {
    const {selected, props: {feature}} = this

    return selected.size ? `Add another ${feature}` : `${feature} by`
  }

  @action onClear = () => {
    const {selectedValues, props: {onSelectMenuItem, onClear}} = this
    selectedValues.map((value: ChangeLogFilterFieldConfig) => {
      const {workspace, field, type} = value,
        key = `${workspace}.${field ?? type}`
      onSelectMenuItem(key, value)
    })
    if (onClear) onClear()
  }

  @computed get feature() {
    return this.props.feature
  }
}

export const ContentTitleBarItemStoreContext = React.createContext<ContentTitleBarItemStore>(null)
export const useContentTitleBarItemStore = () => React.useContext(ContentTitleBarItemStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  onSelectMenuItem: (key, field: ChangeLogFilterFieldConfig, selectedField?: ChangeLogFilterFieldConfig) => void
  selected: any
  feature?: 'filter' | 'sort'
  title?: string
  onClear?: Function
  sortWithOrder?: Array<ISortWithOrder>
  indexes?: Array<string>
}

export const ContentTitleBarItemFieldList = observer<{ selectedField?: ChangeLogFilterFieldConfig }>(function contentTitleBarItemFieldList({selectedField}) {
  const contentTitleBarItemStore = useContentTitleBarItemStore(),
    {selected, searchText, fieldsList, onSelectMenuItem} = contentTitleBarItemStore

  return (
    <>
      {fieldsList.map(f => <ContentTitleBarItemField key={`${f.workspace}.${f.field ?? f.type}`}
                                                     field={f}
                                                     selectedField={selectedField}
                                                     onSelectMenuItem={onSelectMenuItem}
                                                     searchText={searchText}
                                                     renderTo={'all'}
                                                     selected={selected}/>).filter(_.identity)}
    </>
  )
})

export const ContentTitleBarItem = observer<MyProps>(function ContentTitleBarItem({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ContentTitleBarItemStore(stores, obsProps)),
    {onClickOpenMenuItemList, selectedValues, selected, menuTitle, onClear, props: {onSelectMenuItem}, feature, title, searchText, onSearch} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ContentTitleBarItemStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <Card elevation={1} className={css.menu} data-type={'menu-card'}>
          <CardContent data-type={'menu-content'}>
            {selectedValues.length > 0 && feature !== 'sort' && <span className={css.clear} onClick={onClear}>Clear All</span>}
            {selectedValues.map((field: ChangeLogFilterFieldConfig, index) => (
              <ChangeLogMenuItemSelected onSearch={onSearch} searchText={searchText} index={index} feature={feature} onSelectMenuItem={onSelectMenuItem} key={field.field ?? field.type} field={field}/>
            ))}
            {(feature !== 'sort' || selectedValues.length === 0) && <div data-type={'menu-sections'}>
				<SearchPopover onSearch={onSearch} searchText={searchText} text={menuTitle} selectedEl={selected?.length > 0 && selected.map(f => <ContentTitleBarItemField key={`${f.workspace}.${f.field ?? f.type}`}
                                                                                                                                                                            field={f}
                                                                                                                                                                            renderTo={'applied'}
                                                                                                                                                                            onSelectMenuItem={onSelectMenuItem}
                                                                                                                                                                            searchText={searchText}
                                                                                                                                                                            selected={selected}/>)}>
					<ContentTitleBarItemFieldList/>
				</SearchPopover>
              {selectedValues.length === 0 && <div className={css.menuFilter}>No {title} applied</div>}
			</div>}
          </CardContent>
        </Card>
      </div>
    </ContentTitleBarItemStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/titleBarItem/ContentTitleBarItem.tsx